import { useState, useEffect } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client/react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RESET_PASSWORD_MUTATION, VALIDATE_RESET_TOKEN_QUERY } from '../../../graphql/client';

export type TokenStatus = 'CHECKING' | 'VALID' | 'ALREADY_RESET' | 'EXPIRED' | 'INVALID';

export const useRecoverPassword = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [tokenStatus, setTokenStatus] = useState<TokenStatus>('CHECKING');
  const [hasValidated, setHasValidated] = useState(false);

  const [validateToken, { loading: validatingToken }] = useLazyQuery(VALIDATE_RESET_TOKEN_QUERY, {
    fetchPolicy: 'network-only',
  });

  // Extract token and email from URL parameters and validate token
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get('token');
    const emailParam = params.get('email');
    if (tokenParam) setToken(tokenParam);
    if (emailParam) setEmail(emailParam);

    if (tokenParam && emailParam && !hasValidated) {
      setHasValidated(true);
      validateToken({
        variables: { token: tokenParam, email: emailParam },
        context: { skipPersistedQueries: true },
      }).then((res: any) => {
        const data = res?.data;
        const status = data?.validateResetToken?.status;
        if (status === 'ALREADY_RESET') {
          setTokenStatus('ALREADY_RESET');
        } else if (status === 'EXPIRED') {
          setTokenStatus('EXPIRED');
        } else if (status === 'VALID') {
          setTokenStatus('VALID');
        } else {
          setTokenStatus('INVALID');
        }
      }).catch(() => {
        setTokenStatus('INVALID');
      });
    } else if (!tokenParam || !emailParam) {
      setTokenStatus('INVALID');
    }
  }, [location.search, validateToken, hasValidated]);

  const [resetPasswordMutation, { loading }] = useMutation(RESET_PASSWORD_MUTATION, {
    context: {
      skipPersistedQueries: true,
    },
    onCompleted: (data: any) => {
      if (data && data.resetPassword) {
        setMessage(t('passwordResetSuccess', 'Your password has been successfully reset.'));
        setErrorMessage('');
        setTokenStatus('ALREADY_RESET');
      } else {
        setErrorMessage(t('passwordResetError', 'Failed to reset password. The link may be invalid or expired.'));
      }
    },
    onError: (error: any) => {
      const errMsg = error.message || '';
      if (errMsg.toLowerCase().includes('already reset')) {
        setTokenStatus('ALREADY_RESET');
      } else if (errMsg.toLowerCase().includes('no longer valid') || errMsg.toLowerCase().includes('expired')) {
        setTokenStatus('EXPIRED');
      } else {
        setErrorMessage(errMsg || t('passwordResetError', 'Failed to reset password. The link may be invalid or expired.'));
      }
      setMessage('');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setMessage('');

    if (!password || !passwordConfirmation) {
      setErrorMessage(t('fieldsRequired', 'All fields are required.'));
      return;
    }

    if (password !== passwordConfirmation) {
      setErrorMessage(t('passwordMismatch', 'Passwords do not match.'));
      return;
    }
    
    if (password.length < 8) {
      setErrorMessage(t('passwordTooShort', 'Password must be at least 8 characters long.'));
      return;
    }

    resetPasswordMutation({
      variables: { 
        token,
        email,
        password,
        password_confirmation: passwordConfirmation
      },
    });
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  return {
    t,
    i18n,
    email,
    token,
    tokenStatus,
    validatingToken,
    password,
    setPassword,
    passwordConfirmation,
    setPasswordConfirmation,
    message,
    errorMessage,
    loading,
    handleSubmit,
    changeLanguage,
  };
};
